import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import "./Result.css";

// Helper function to format run text
const formatRunText = (rdesc) => {
  if (!rdesc) return "";
  // rdesc format: "Run 2", "Run 3", etc.
  return rdesc;
};

// Helper function to map win value to display text
const getDisplayText = (win) => {
  if (!win) return "";
  // win values: "2", "3", "4", "5", "6", "7", "8", "9", "10" represent runs
  return win;
};

const ResultCt20 = ({
  data = [],
  gameData = {},
  gameType = 'cmatch20',
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults(gameType);

        // Handle res array structure (cmatch20 API format)
        if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win || "",
            displayText: getDisplayText(item.win)
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, [gameType]);

  const handleResultClick = useCallback(async (result, index) => {
    setSelectedResult(result);
    setLoading(true);

    // Find the mid for this result
    let mid = null;
    if (typeof result === 'object' && result.mid) {
      mid = result.mid;
    } else if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      // For older results, try to get from gameData or use a default
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }

    // Fetch detail results if we have a mid
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults(gameType, mid.toString());
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        } else if (response?.data?.t1) {
          setDetailResult(response.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  }, [gameType, lastResults, data, gameData, detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    // Use detailResult if available, otherwise use selectedResult data
    const resultData = detailResult || (typeof selectedResult === 'object' ? selectedResult.fullData : null);

    if (!resultData) {
      // Fallback to basic data
      const winValue = typeof selectedResult === 'object' ? selectedResult.win : selectedResult;
      return {
        roundId: gameData?.data?.data?.mid || gameData?.mid || "N/A",
        matchTime: gameData?.data?.data?.mt || new Date().toLocaleString(),
        runText: winValue ? `Run ${winValue}` : "",
        win: winValue || "",
        card: null,
      };
    }

    const rdescText = formatRunText(resultData.rdesc);

    return {
      roundId: resultData.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: resultData.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      runText: rdescText || (resultData.win ? `Run ${resultData.win}` : ""),
      win: resultData.win || "",
      card: resultData.card || null,
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Get card image path
  const getCardImage = (cardCode) => {
    if (!cardCode) return null;
    return `/assets/img/cards/${cardCode}.png`;
  };

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : (data.length > 0 ? data.map(r => ({ win: r })) : []);

  // Get ball image path based on win value
  const getBallImage = (win) => {
    if (!win) return null;
    const winValue = win.toString();
    return `/assets/img/ct20/balls/ball${winValue}.png`;
  };

  return (
    <>
      <div className="last-result-container text-right m-t-10">
        {allResults.map((res, index) => {
          const winValue = typeof res === 'object' ? (res.win || "") : res;
          const resultObj = typeof res === 'object' ? res : (lastResults[index] || { win: res });
          const ballImageSrc = getBallImage(winValue);
          const displayText = winValue ? `Run ${winValue}` : "";

          return (
            <span
              key={index}
              className="last-result"
              onClick={() => handleResultClick(resultObj, index)}
              style={{ cursor: 'pointer' }}
              title={displayText}
            >
              {ballImageSrc && (
                <img 
                  src={ballImageSrc} 
                  className="balls" 
                  alt={displayText}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </span>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="C MATCH 20 Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          {/* Result Details */}
          <div>
            <h6 className="text-right round-id">
              <b>Round Id:</b> {modalData.roundId}
            </h6>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

          <div className="cc20-img">
                  {modalData.card && (
                    <img 
                      src={getCardImage(modalData.card)} 
                      alt={`Card ${modalData.card}`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
             
            </div>
          </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultCt20;

